import { useRef, useState } from "react"
import axios from "axios"
import { CheckCircle2, Loader2, Upload as UploadIcon, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

/**
 * Result returned by /upload_video — the same shape the legacy admin
 * stored as `S3_List` / `FileName` / `S30_List`. Used by NewVideo (set_video
 * payload) and ChapterForm (chapter_create payload `s30_data`).
 *
 * `s3_30sec_data` is the 30-second preview the upload service generates
 * server-side via ffmpeg. Optional because the service returns null for
 * unsupported file types (we shipped the null-fix in M6/S11).
 */
export type UploadResult = {
  s3response: unknown
  file_name: string
  s3_30sec_data?: unknown
  /** Browser-generated poster frame uploaded to S3. Present when the
   *  source is a video and the browser could decode it. Shape mirrors
   *  s3response ({ Location, Key, location, key }). */
  thumb_s3response?: unknown
}

// Grab a poster frame from the chosen video in the browser. Best-effort:
// returns null on any decode failure (e.g. an obscure codec) — the
// upload still succeeds without a thumbnail in that case.
//
// Multi-attempt + brightness check: the previous single-seek-at-1s
// version frequently landed on a black intro frame because the seek
// was capped at 1 second for every video. Now samples up to 4 seek
// positions and keeps the BRIGHTEST result, so a dark/black intro
// can't pin us — we always end up with the best frame the player
// could decode within the 12s budget.
function generateVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("video/")) {
      resolve(null)
      return
    }
    const url = URL.createObjectURL(file)
    const v = document.createElement("video")
    v.preload = "metadata"
    v.muted = true
    v.playsInline = true
    v.src = url
    let settled = false
    const done = (b: Blob | null) => {
      if (settled) return
      settled = true
      try { URL.revokeObjectURL(url) } catch {}
      resolve(b)
    }

    // Seek positions tuned to dodge intros / outros:
    //   ≥ 20s → 25 / 50 / 75% (true mid-clip frames)
    //   ≥  5s → 40 / 60 / 80%
    //   ≥  2s → 50 / 70 / 30%
    //   <  2s → midpoint only
    const seekTimes = (dur: number): number[] => {
      if (!isFinite(dur) || dur <= 0) return [0.1]
      if (dur >= 20) return [dur * 0.25, dur * 0.5, dur * 0.75, dur * 0.15]
      if (dur >= 5)  return [dur * 0.4,  dur * 0.6, dur * 0.8,  dur * 0.2]
      if (dur >= 2)  return [dur * 0.5,  dur * 0.7, dur * 0.3]
      return [Math.max(0.1, dur * 0.5)]
    }

    let times: number[] = []
    let attempt = 0
    // Track the brightest successful capture in case all samples are
    // dark — a dim frame is still better than no thumbnail.
    let bestBlob: Blob | null = null
    let bestBrightness = -1
    const ACCEPTABLE_BRIGHTNESS = 30 // ~12% luminance; clearly non-black
    const TARGET_BRIGHTNESS = 60     // good enough to short-circuit retries

    v.addEventListener("loadedmetadata", () => {
      times = seekTimes(v.duration)
      tryNext()
    })

    function tryNext() {
      if (attempt >= times.length) return done(bestBlob)
      const t = times[attempt++]
      try {
        v.currentTime = t
      } catch {
        done(bestBlob)
      }
    }

    v.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas")
        canvas.width = v.videoWidth || 1280
        canvas.height = v.videoHeight || 720
        const ctx = canvas.getContext("2d")
        if (!ctx) return done(bestBlob)
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height)

        // Sample luminance on a sparse grid (cheap; ~1k samples).
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data
        let sum = 0
        let n = 0
        const step = Math.max(4, Math.floor(img.length / 4000)) * 4
        for (let i = 0; i < img.length; i += step) {
          sum += img[i] * 0.299 + img[i + 1] * 0.587 + img[i + 2] * 0.114
          n++
        }
        const brightness = n > 0 ? sum / n : 0

        canvas.toBlob(
          (b) => {
            if (b && brightness > bestBrightness) {
              bestBlob = b
              bestBrightness = brightness
            }
            // Short-circuit if this frame is clearly good.
            if (brightness >= TARGET_BRIGHTNESS) return done(b)
            // Otherwise try the next seek; if exhausted, return best.
            if (attempt < times.length) {
              tryNext()
            } else {
              done(bestBlob)
            }
          },
          "image/jpeg",
          0.85
        )

        // Avoid a "good enough" early-exit when the very first frame
        // is marginal (between ACCEPTABLE and TARGET) — toBlob is
        // async, so the decision happens in its callback above.
        void ACCEPTABLE_BRIGHTNESS
      } catch {
        done(bestBlob)
      }
    })

    v.addEventListener("error", () => done(bestBlob))
    // Hard timeout — never let a stuck decode block the upload. Bumped
    // to 12s so the multi-seek loop has room to complete on slower
    // devices / longer source videos.
    setTimeout(() => done(bestBlob), 12000)
  })
}

/**
 * VideoFileDrop — encapsulates the legacy "upload a file then keep its
 * S3 metadata for the metadata POST" flow. POSTs multipart to
 * `${UPLOAD_BASE_URL}/upload_video`.
 *
 * Behavior parity with legacy UploadVideo.jsx + Milestone fixes:
 *   - M3 / A2: Submit is blocked while upload is in progress (the parent
 *     reads `isUploading` via the `onChange` callback).
 *   - M5 / A8: Authorization header is the real Bearer JWT, not the
 *     `"authorization-text"` placeholder.
 *
 * Improvements over legacy (no contract change):
 *   - Real progress bar via axios.onUploadProgress
 *   - Drag-and-drop in addition to click-to-pick
 *   - Cancel-in-flight via AbortController
 *   - Inline error message instead of a fleeting toast
 */
export function VideoFileDrop({
  accept = "video/*,audio/*",
  result,
  onResult,
  onUploadingChange,
  onError,
  disabled,
}: {
  accept?: string
  result: UploadResult | null
  onResult: (r: UploadResult | null) => void
  onUploadingChange?: (uploading: boolean) => void
  onError?: (err: string | null) => void
  disabled?: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [chosenName, setChosenName] = useState<string | null>(null)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const setUploading = (v: boolean) => {
    setIsUploading(v)
    onUploadingChange?.(v)
  }
  const setError = (v: string | null) => {
    setErrMsg(v)
    onError?.(v)
  }

  const beginUpload = async (file: File) => {
    setError(null)
    setProgress(0)
    setChosenName(file.name)
    setUploading(true)

    const ac = new AbortController()
    abortRef.current = ac

    try {
      // BUGFIX: the old code POSTed multipart to
      // `${UPLOAD_BASE_URL}/upload_video` — i.e. upload.goodtalk.coach,
      // a host the browser never actually reaches (confirmed: this
      // request never hit nginx and no video was ever created). The
      // whole rest of the admin works against API_BASE_URL.
      //
      // New flow uses the working API host + the deployed backend
      // endpoints: ask /admin/presign_video for a short-lived S3 PUT
      // URL, upload the file DIRECTLY to S3 (single hop — also the
      // fastest possible path for large videos), then hand the
      // resulting { Location, Key } to set_video as `s3_data`.
      const ext = file.name.includes(".")
        ? file.name.slice(file.name.lastIndexOf(".") + 1)
        : ""
      const presignRes = await api.post<{
        success: boolean
        message: string
        data: {
          uploadUrl: string
          key: string
          publicUrl: string
          contentType: string
        }
      }>("/admin/presign_video", {
        extension: ext,
        contentType: file.type || undefined,
      })
      const p = presignRes.data?.data
      if (!p || !p.uploadUrl || !p.key || !p.publicUrl) {
        throw new Error("Could not get an upload URL from the server")
      }

      // Direct PUT to S3 — raw axios (NOT the `api` instance: no auth
      // header, no baseURL; the presigned URL is self-authorizing).
      await axios.put(p.uploadUrl, file, {
        headers: { "Content-Type": p.contentType || file.type || "application/octet-stream" },
        signal: ac.signal,
        onUploadProgress: (e) => {
          if (e.total) {
            setProgress(Math.round((e.loaded * 100) / e.total))
          }
        },
      })

      // Best-effort: extract a poster frame from the video in the
      // browser and upload it via another presigned PUT. Skipped for
      // audio-only files; failure → no thumbnail, upload still
      // succeeds.
      let thumb_s3response: UploadResult["thumb_s3response"]
      try {
        const thumbBlob = await generateVideoThumbnail(file)
        if (thumbBlob) {
          const tp = await api.post<{
            success: boolean
            message: string
            data: { uploadUrl: string; key: string; publicUrl: string; contentType: string }
          }>("/admin/presign_video", {
            extension: "jpg",
            contentType: "image/jpeg",
          })
          const td = tp.data?.data
          if (td?.uploadUrl) {
            await axios.put(td.uploadUrl, thumbBlob, {
              headers: { "Content-Type": "image/jpeg" },
            })
            thumb_s3response = {
              Location: td.publicUrl,
              Key: td.key,
              location: td.publicUrl,
              key: td.key,
            }
          }
        }
      } catch (e) {
        // non-fatal — the main upload already succeeded
        console.warn("thumbnail generation failed (non-fatal):", e)
      }

      onResult({
        // Emit every casing legacy consumers read: set_video uses
        // .Location/.Key; purpose_service uses lowercase .key;
        // others use .location. Covers all admin endpoints.
        s3response: {
          Location: p.publicUrl,
          Key: p.key,
          location: p.publicUrl,
          key: p.key,
        },
        file_name: file.name,
        thumb_s3response,
      })
    } catch (err) {
      if (axios.isCancel(err)) {
        setChosenName(null)
        return
      }
      const msg = err instanceof Error ? err.message : "Upload failed"
      setError(msg)
      onResult(null)
    } finally {
      setUploading(false)
      abortRef.current = null
    }
  }

  const cancel = () => {
    abortRef.current?.abort()
  }

  const reset = () => {
    onResult(null)
    setChosenName(null)
    setProgress(0)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (disabled) return
          const f = e.dataTransfer.files?.[0]
          if (f) void beginUpload(f)
        }}
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input p-6 text-center text-sm text-muted-foreground transition-colors",
          isDragOver && "border-primary bg-primary/5",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        <UploadIcon className="h-6 w-6 text-muted-foreground" />
        <div>
          {result ? (
            <span className="text-foreground">
              Uploaded — ready to submit. Click to replace.
            </span>
          ) : isUploading ? (
            <span>Uploading {chosenName} — {progress}%</span>
          ) : (
            <>
              Drop a video or audio file here, or{" "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                choose file
              </button>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          disabled={disabled || isUploading}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void beginUpload(f)
          }}
        />
      </div>

      {/* Progress bar */}
      {isUploading && (
        <div className="space-y-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={cancel}
              className="h-7 text-xs"
            >
              Cancel upload
            </Button>
          </div>
        </div>
      )}

      {/* Done state with the file name + replace */}
      {!isUploading && result && (
        <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="font-medium">{result.file_name}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {errMsg && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {errMsg}
        </div>
      )}

      {isUploading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Don't navigate away until this finishes.
        </div>
      )}
    </div>
  )
}
